/**
 * Thesis selection grid for deployment wizard Step 1.
 *
 * Shows all theses with their allocation status, highlighting
 * those that are under-allocated.
 */
import type { ThesisAllocation } from '../../types/thesis'

interface ThesisSelectorProps {
  allocations: ThesisAllocation[]
  selectedThesisId: string | null
  onSelect: (thesisId: string) => void
  isLoading?: boolean
}

export function ThesisSelector({
  allocations,
  selectedThesisId,
  onSelect,
  isLoading,
}: ThesisSelectorProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 bg-gray-100 rounded-lg animate-pulse"
          />
        ))}
      </div>
    )
  }

  if (allocations.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No theses found. Create a thesis first to deploy capital.</p>
      </div>
    )
  }

  // Sort: under-allocated first, then by name
  const sorted = [...allocations].sort((a, b) => {
    // Under-allocated (negative drift) first
    if (a.drift_pct < 0 && b.drift_pct >= 0) return -1
    if (a.drift_pct >= 0 && b.drift_pct < 0) return 1
    // Then by name
    return a.thesis_name.localeCompare(b.thesis_name)
  })

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {sorted.map((allocation) => {
        const isSelected = selectedThesisId === allocation.thesis_id
        const isUnderAllocated = allocation.drift_pct < 0

        return (
          <button
            key={allocation.thesis_id}
            onClick={() => onSelect(allocation.thesis_id)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              isSelected
                ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200'
                : isUnderAllocated
                ? 'border-amber-300 bg-amber-50 hover:border-amber-400'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-medium text-gray-900 truncate">
                {allocation.thesis_name}
              </h3>
              {isUnderAllocated && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                  Under
                </span>
              )}
            </div>

            {/* Allocation bar */}
            <div className="mb-2">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Current: {allocation.current_allocation_pct.toFixed(1)}%</span>
                <span>Target: {allocation.target_allocation_pct?.toFixed(1) ?? '-'}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    allocation.drift_status === 'ok'
                      ? 'bg-green-500'
                      : allocation.drift_status === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                  }`}
                  style={{
                    width: `${Math.min(100, allocation.current_allocation_pct)}%`,
                  }}
                />
              </div>
            </div>

            {/* Drift info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {allocation.holdings_count} holding{allocation.holdings_count !== 1 ? 's' : ''}
              </span>
              <span
                className={
                  allocation.drift_pct < 0
                    ? 'text-amber-600 font-medium'
                    : allocation.drift_pct > 0
                    ? 'text-blue-600'
                    : 'text-gray-500'
                }
              >
                {allocation.drift_pct > 0 ? '+' : ''}
                {allocation.drift_pct.toFixed(1)}% drift
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
